import { formatMinor, type InvoiceDocument } from '@hvac/contracts';

/**
 * The invoice as the customer sees it. Rendered the same on screen and on
 * paper; the print page strips the chrome around it and nothing else.
 */
export function InvoiceDocumentView({ document }: { document: InvoiceDocument }) {
  const { invoice, lines, customer, business } = document;
  const rate = invoice.taxRate === null ? null : Number(invoice.taxRate);
  const taxLabel =
    rate === null || rate === 0
      ? 'Tax'
      : `${business.province === 'AB' || business.province === 'QC' || ['BC', 'SK', 'MB'].includes(business.province) ? 'GST' : 'HST'} ${String(Math.round(rate * 1000) / 10)}%`;

  return (
    <div className="mx-auto w-full max-w-3xl bg-white p-8 text-[13px] leading-relaxed text-black print:p-0">
      <header className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{business.name}</h1>
          {business.address === null ? null : (
            <p className="mt-1 whitespace-pre-line text-neutral-600">{business.address}</p>
          )}
          <p className="text-neutral-600">
            {[business.phone, business.email].filter((part) => part !== null).join(' · ')}
          </p>
          {business.businessNumber === null ? null : (
            <p className="text-neutral-600">GST/HST No. {business.businessNumber}</p>
          )}
        </div>
        <div className="text-right">
          <p className="text-3xl font-semibold tracking-tight">
            {invoice.status === 'draft' ? 'DRAFT' : 'INVOICE'}
          </p>
          {invoice.invoiceNumber === null ? null : (
            <p className="mt-1 text-base">{invoice.invoiceNumber}</p>
          )}
          <p className="mt-2 text-neutral-600">Dated {invoice.issuedOn}</p>
          {invoice.dueOn === null ? null : <p className="text-neutral-600">Due {invoice.dueOn}</p>}
          {invoice.status === 'void' ? (
            <p className="mt-2 inline-block border border-red-600 px-2 py-0.5 text-sm font-semibold text-red-600">
              VOID
            </p>
          ) : null}
        </div>
      </header>

      <section className="mt-8">
        <p className="text-xs uppercase tracking-wide text-neutral-500">Bill to</p>
        <p className="mt-1 font-medium">{customer.legalName ?? customer.displayName}</p>
        {customer.legalName !== null && customer.legalName !== customer.displayName ? (
          <p>{customer.displayName}</p>
        ) : null}
        {[customer.addressLine1, customer.addressLine2]
          .filter((part) => part !== null)
          .map((part) => (
            <p key={part}>{part}</p>
          ))}
        <p>
          {[customer.city, customer.province, customer.postalCode]
            .filter((part) => part !== null && part !== '')
            .join(' ')}
        </p>
        <p className="text-neutral-600">
          {[customer.email, customer.phone].filter((part) => part !== null).join(' · ')}
        </p>
        {customer.isTaxExempt ? (
          <p className="text-neutral-600">
            Tax exempt{customer.gstHstNumber === null ? '' : ` · ${customer.gstHstNumber}`}
          </p>
        ) : null}
      </section>

      <table className="mt-8 w-full tabular">
        <thead>
          <tr className="border-b border-black text-left text-xs uppercase tracking-wide text-neutral-500">
            <th className="py-1 font-medium">Description</th>
            <th className="py-1 text-right font-medium">Qty</th>
            <th className="py-1 text-right font-medium">Unit</th>
            <th className="py-1 text-right font-medium">Amount</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.id} className="border-b border-neutral-200 align-top">
              <td className="py-1.5 pr-4">
                {line.description}
                {line.taxable ? '' : <span className="text-neutral-500"> (no tax)</span>}
              </td>
              <td className="py-1.5 text-right">{trimQuantity(line.quantity)}</td>
              <td className="py-1.5 text-right">{formatMinor(line.unitPriceMinor)}</td>
              <td className="py-1.5 text-right">{formatMinor(line.netMinor)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-4 ml-auto w-64">
        <Row label="Subtotal" value={formatMinor(invoice.netMinor)} />
        <Row label={taxLabel} value={formatMinor(invoice.taxMinor)} />
        <Row label="Total" value={formatMinor(invoice.grossMinor)} strong />
        {invoice.status === 'draft' || invoice.receivedMinor === '0' ? null : (
          <>
            <Row label="Received" value={formatMinor(invoice.receivedMinor)} />
            <Row label="Balance due" value={formatMinor(invoice.owedMinor)} strong />
          </>
        )}
      </div>

      {invoice.notes === null ? null : (
        <section className="mt-8">
          <p className="text-xs uppercase tracking-wide text-neutral-500">Notes</p>
          <p className="mt-1 whitespace-pre-line">{invoice.notes}</p>
        </section>
      )}

      {business.footer === null ? null : (
        <footer className="mt-10 border-t border-neutral-200 pt-3 text-xs text-neutral-600">
          <p className="whitespace-pre-line">{business.footer}</p>
        </footer>
      )}
    </div>
  );
}

function Row({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return (
    <div
      className={[
        'flex justify-between py-0.5',
        strong ? 'border-t border-black font-semibold' : '',
      ].join(' ')}
    >
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

/** "2.000" prints as 2; "1.500" as 1.5. */
export function trimQuantity(quantity: string): string {
  return quantity.includes('.') ? quantity.replace(/\.?0+$/, '') : quantity;
}
